import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Eye, TrendingDown, TrendingUp } from 'lucide-react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

interface CategoryTrendData {
  id: string;
  categoryId: string;
  categoryName: string;
  month: string;
  totalAmount: number;
  transactionCount: number;
  growthRate?: number;
  yearOverYearGrowth?: number;
  percentOfTotal?: number;
  forecast?: number;
}

interface CategoryTrendsChartProps {
  data: CategoryTrendData[];
}

const MODERN_COLORS = [
  '#6366f1', // indigo
  '#8b5cf6', // violet
  '#ec4899', // pink
  '#06b6d4', // cyan
  '#10b981', // emerald
  '#f59e0b', // amber
  '#ef4444', // red
  '#84cc16', // lime
  '#3b82f6', // blue
  '#f97316', // orange
  '#6b7280', // gray
  '#14b8a6', // teal
];

export function CategoryTrendsChart({ data }: CategoryTrendsChartProps) {
  if (!data || data.length === 0) {
    return (
      <div className="flex h-64 items-center justify-center text-muted-foreground">
        <div className="text-center space-y-2">
          <div className="h-16 w-16 rounded-full bg-muted/50 mx-auto flex items-center justify-center">
            📈
          </div>
          <p>Nenhum dado de categoria disponível</p>
        </div>
      </div>
    );
  }

  // Get unique categories
  const categoryIds = Array.from(new Set(data.map((item) => item.categoryId)));
  const categories = categoryIds.map((categoryId, index) => {
    const item = data.find((d) => d.categoryId === categoryId);
    return {
      id: categoryId,
      name: item?.categoryName || 'Unknown',
      color: MODERN_COLORS[index % MODERN_COLORS.length],
    };
  });

  // Prepare data for area chart (stacked)
  const monthlyData = data.reduce((acc: any[], item) => {
    const monthKey = format(parseISO(item.month), 'MMM yyyy', { locale: ptBR });
    let monthEntry = acc.find((entry) => entry.month === monthKey);

    if (!monthEntry) {
      monthEntry = {
        month: monthKey,
        date: parseISO(item.month),
        displayMonth: format(parseISO(item.month), 'MM/yyyy'),
      };
      acc.push(monthEntry);
    }

    monthEntry[item.categoryName] = item.totalAmount;
    return acc;
  }, []);

  const sortedMonthlyData = monthlyData.sort(
    (a, b) => a.date.getTime() - b.date.getTime()
  );

  // Prepare data for pie chart (latest month distribution)
  const latestMonth = sortedMonthlyData[sortedMonthlyData.length - 1];
  const pieData = categories
    .map((category, index) => ({
      name: category.name,
      value: latestMonth?.[category.name] || 0,
      color: MODERN_COLORS[index % MODERN_COLORS.length],
    }))
    .filter((item) => item.value > 0)
    .sort((a, b) => b.value - a.value);

  // Get top categories for performance summary
  const topCategories = categories
    .map(category => {
      const categoryData = data.filter(item => item.categoryId === category.id);
      const totalSpent = categoryData.reduce((sum, item) => sum + item.totalAmount, 0);
      const latestData = categoryData.sort((a, b) => new Date(b.month).getTime() - new Date(a.month).getTime())[0];

      return {
        ...category,
        totalSpent,
        latestGrowth: latestData?.growthRate || 0,
        transactions: categoryData.reduce((sum, item) => sum + item.transactionCount, 0)
      };
    })
    .sort((a, b) => b.totalSpent - a.totalSpent)
    .slice(0, 6);

  const formatCurrency = (value: number) => {
    return `R$ ${value.toLocaleString('pt-BR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })}`;
  };

  return (
    <div className="space-y-8">
      {/* Top Categories Performance */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {topCategories.slice(0, 3).map((category, index) => (
          <div key={category.id} className="relative overflow-hidden rounded-xl border bg-card p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h4 className="font-semibold text-lg">{category.name}</h4>
                <p className="text-muted-foreground text-sm">{category.transactions} transações</p>
              </div>
              <div
                className="h-3 w-3 rounded-full"
                style={{ backgroundColor: category.color }}
              />
            </div>

            <div className="space-y-2">
              <div className="text-2xl font-bold">
                {formatCurrency(category.totalSpent)}
              </div>

              <div className={`flex items-center gap-1 text-sm ${
                category.latestGrowth >= 0 ? 'text-red-500' : 'text-green-500'
              }`}>
                {category.latestGrowth >= 0 ? (
                  <TrendingUp className="h-4 w-4" />
                ) : (
                  <TrendingDown className="h-4 w-4" />
                )}
                {Math.abs(category.latestGrowth).toFixed(1)}% último mês
              </div>
            </div>

            <div className="absolute -right-2 -bottom-2 h-20 w-20 rounded-full opacity-10"
                 style={{ backgroundColor: category.color }} />
          </div>
        ))}
      </div>

      {/* Monthly Evolution Chart */}
      <div className="rounded-xl border bg-card p-6">
        <div className="mb-6">
          <h3 className="text-xl font-semibold">Evolução por Categoria</h3>
          <p className="text-muted-foreground text-sm">Tendência de gastos ao longo do tempo por categoria</p>
        </div>

        <div className="h-96">
          <ResponsiveContainer height="100%" width="100%">
            <AreaChart
              data={sortedMonthlyData}
              margin={{
                top: 20,
                right: 30,
                left: 20,
                bottom: 20,
              }}
            >
              <defs>
                {categories.slice(0, 6).map((category, index) => (
                  <linearGradient key={category.id} id={`color${category.id}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={category.color} stopOpacity={0.6}/>
                    <stop offset="95%" stopColor={category.color} stopOpacity={0.1}/>
                  </linearGradient>
                ))}
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" strokeOpacity={0.3} />
              <XAxis
                dataKey="displayMonth"
                tick={{ fontSize: 12, fill: 'hsl(var(--foreground))' }}
                axisLine={{ stroke: 'hsl(var(--border))' }}
              />
              <YAxis
                tick={{ fontSize: 12, fill: 'hsl(var(--foreground))' }}
                tickFormatter={formatCurrency}
                axisLine={{ stroke: 'hsl(var(--border))' }}
              />
              <Tooltip
                content={({ active, payload, label }) => {
                  if (active && payload && payload.length) {
                    return (
                      <div className="rounded-lg border bg-background p-4 shadow-lg max-w-xs">
                        <p className="font-semibold mb-2">{`Mês: ${label}`}</p>
                        <div className="space-y-1">
                          {payload
                            .filter((entry) => entry.value && entry.value > 0)
                            .sort((a, b) => Number(b.value) - Number(a.value))
                            .map((entry, index) => (
                            <div key={index} className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <div
                                  className="h-2 w-2 rounded-full"
                                  style={{ backgroundColor: entry.color }}
                                />
                                <span className="text-sm">{entry.dataKey}</span>
                              </div>
                              <span className="text-sm font-medium">
                                R$ {Number(entry.value).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  }
                  return null;
                }}
              />
              <Legend />
              {categories.slice(0, 6).map((category, index) => (
                <Area
                  key={category.id}
                  type="monotone"
                  dataKey={category.name}
                  stackId="1"
                  stroke={category.color}
                  strokeWidth={2}
                  fill={`url(#color${category.id})`}
                  connectNulls={false}
                />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Distribution Pie Chart */}
        <div className="rounded-xl border bg-card p-6">
          <div className="mb-4">
            <h4 className="text-lg font-semibold">Distribuição Atual</h4>
            <p className="text-muted-foreground text-sm">
              Participação de cada categoria ({latestMonth?.month})
            </p>
          </div>

          <div className="h-80">
            <ResponsiveContainer height="100%" width="100%">
              <PieChart>
                <Pie
                  cx="50%"
                  cy="50%"
                  data={pieData}
                  dataKey="value"
                  nameKey="name"
                  outerRadius={120}
                  innerRadius={40}
                  paddingAngle={2}
                  label={({ name, percent }) =>
                    percent > 5 ? `${name}: ${(percent * 100).toFixed(1)}%` : ''
                  }
                  labelLine={false}
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      const data = payload[0].payload;
                      return (
                        <div className="rounded-lg border bg-background p-3 shadow-lg">
                          <p className="font-semibold">{data.name}</p>
                          <p className="text-sm">
                            R$ {data.value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {((data.value / pieData.reduce((sum, item) => sum + item.value, 0)) * 100).toFixed(1)}% do total
                          </p>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Category Performance Table */}
        <div className="rounded-xl border bg-card p-6">
          <div className="mb-4">
            <h4 className="text-lg font-semibold">Performance das Categorias</h4>
            <p className="text-muted-foreground text-sm">Crescimento e participação</p>
          </div>

          <div className="space-y-3 max-h-80 overflow-y-auto">
            {topCategories.map((category) => {
              const latestData = data
                .filter((item) => item.categoryId === category.id)
                .sort((a, b) => new Date(b.month).getTime() - new Date(a.month).getTime())[0];

              return (
                <div key={category.id} className="rounded-lg border bg-muted/30 p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <div
                        className="h-3 w-3 rounded-full"
                        style={{ backgroundColor: category.color }}
                      />
                      <span className="font-medium">{category.name}</span>
                    </div>
                    <Eye className="h-4 w-4 text-muted-foreground" />
                  </div>

                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">Total</p>
                      <p className="font-semibold">{formatCurrency(category.totalSpent)}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Crescimento</p>
                      <div className={`flex items-center gap-1 ${
                        category.latestGrowth >= 0 ? 'text-red-500' : 'text-green-500'
                      }`}>
                        {category.latestGrowth >= 0 ? (
                          <TrendingUp className="h-3 w-3" />
                        ) : (
                          <TrendingDown className="h-3 w-3" />
                        )}
                        <span className="font-semibold">
                          {Math.abs(category.latestGrowth).toFixed(1)}%
                        </span>
                      </div>
                    </div>
                  </div>

                  {latestData?.percentOfTotal && (
                    <div className="mt-2 pt-2 border-t">
                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <span>Participação no total</span>
                        <span>{latestData.percentOfTotal.toFixed(1)}%</span>
                      </div>
                      <div className="mt-1 h-1.5 bg-muted rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all"
                          style={{
                            width: `${Math.min(latestData.percentOfTotal, 100)}%`,
                            backgroundColor: category.color
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Summary Statistics */}
      <div className="rounded-xl border bg-card p-6">
        <div className="mb-4">
          <h4 className="text-lg font-semibold">Resumo Geral</h4>
          <p className="text-muted-foreground text-sm">Principais métricas das categorias</p>
        </div>

        <div className="grid gap-4 md:grid-cols-4">
          <div className="text-center p-4 rounded-lg bg-muted/50">
            <p className="text-2xl font-bold text-blue-600">{categories.length}</p>
            <p className="text-sm text-muted-foreground">Categorias Ativas</p>
          </div>

          <div className="text-center p-4 rounded-lg bg-muted/50">
            <p className="text-2xl font-bold text-emerald-600">
              {formatCurrency(data.reduce((sum, item) => sum + item.totalAmount, 0))}
            </p>
            <p className="text-sm text-muted-foreground">Total Gasto</p>
          </div>

          <div className="text-center p-4 rounded-lg bg-muted/50">
            <p className="text-2xl font-bold text-amber-600">
              {pieData.length > 0 ? pieData[0].name : '-'}
            </p>
            <p className="text-sm text-muted-foreground">Categoria Líder</p>
          </div>

          <div className="text-center p-4 rounded-lg bg-muted/50">
            <p className="text-2xl font-bold text-purple-600">
              {data.reduce((sum, item) => sum + item.transactionCount, 0)}
            </p>
            <p className="text-sm text-muted-foreground">Total Transações</p>
          </div>
        </div>
      </div>
    </div>
  );
}